/**
 * worktreeLabel — derive a short, glanceable label for a single worktree
 * checkout when several share the same logical project key in the sidebar.
 *
 * Examples (`repositorySubpath = "apps/server"`, `repositoryName = "cmmd-forge"`):
 *
 *   /Users/me/.cursor/worktrees/cmmd-forge/3dzk/apps/server
 *     → { primary: "3dzk", namespace: "cursor" }
 *
 *   /Users/me/.codex/worktrees/0e93/cmmd-forge/apps/server
 *     → { primary: "0e93", namespace: "codex" }
 *
 *   /Users/me/Documents/Projects/CMMD/.worktrees/cmmd-forge/trusting-hawking/apps/server
 *     → { primary: "trusting-hawking", namespace: "CMMD" }
 *
 * Strategy:
 *   1. Strip the `repositorySubpath` suffix from the cwd so we work with
 *      the worktree-root path (the directory the user thinks of as "the
 *      checkout").
 *   2. Walk the segments looking for a `worktrees` / `.worktrees` marker.
 *      The slug is the first segment after the marker that ISN'T the repo
 *      name (so layouts that nest the repo dir inside the worktree dir
 *      AND layouts that nest the worktree dir inside the repo dir both
 *      produce the right answer).
 *   3. Namespace = segment immediately before the marker, with any leading
 *      dot stripped (so `.cursor` → `cursor`).
 *   4. Fallback when no marker is found: use the basename of the worktree
 *      root, stepping back past any segment that equals the repo name so
 *      the label is informative rather than just repeating the project.
 *
 * Pure / no IO — safe to test in isolation.
 */

export interface WorktreeLabel {
  /** Short identifier — typically the worktree slug (e.g. `3dzk`). */
  readonly primary: string;
  /** Optional grouping prefix (e.g. `cursor`, `codex`). `null` when absent. */
  readonly namespace: string | null;
}

const WORKTREES_SEGMENT_NAMES: ReadonlySet<string> = new Set(["worktrees", ".worktrees"]);

function splitPosixSegments(input: string): readonly string[] {
  return input
    .replace(/\\/g, "/")
    .split("/")
    .filter((segment) => segment.length > 0);
}

/** Trim the project's repo subpath suffix from its cwd. */
function trimRepoSubpath(cwd: string, repositorySubpath: string | null | undefined): string {
  if (!repositorySubpath || repositorySubpath.trim().length === 0) return cwd;
  const subSegments = splitPosixSegments(repositorySubpath);
  const cwdSegments = splitPosixSegments(cwd);
  // Only trim when the cwd actually ends with the subpath — otherwise
  // something is inconsistent and we'd rather leave the path alone than
  // chop arbitrary segments.
  if (cwdSegments.length < subSegments.length) return cwd;
  for (let i = 0; i < subSegments.length; i++) {
    if (cwdSegments[cwdSegments.length - subSegments.length + i] !== subSegments[i]) {
      return cwd;
    }
  }
  const root = cwdSegments.slice(0, cwdSegments.length - subSegments.length);
  const leading = cwd.startsWith("/") ? "/" : "";
  return leading + root.join("/");
}

function stripLeadingDot(segment: string): string {
  return segment.startsWith(".") ? segment.slice(1) : segment;
}

/**
 * Single-member label computation.
 *
 * `skipSegments` carries segment NAMES that should be treated as
 * structural (e.g. the repo's local directory name); the slug-finding
 * walk skips past any of them. Passing a set of names — rather than a
 * single `repositoryName` — lets the group-aware helper supply BOTH the
 * git-derived repository name AND any directory name that is shared
 * across all members of the group (so layouts that nest the repo dir
 * inside the worktree dir AND layouts that nest the worktree dir inside
 * the repo dir both produce informative labels).
 */
export function deriveWorktreeLabel(
  cwd: string,
  repositorySubpath: string | null | undefined,
  skipSegments: ReadonlySet<string> | null | undefined = null,
): WorktreeLabel {
  const skip = skipSegments ?? new Set<string>();
  const worktreeRoot = trimRepoSubpath(cwd, repositorySubpath);
  const segments = splitPosixSegments(worktreeRoot);
  if (segments.length === 0) {
    return { primary: cwd || "(unknown)", namespace: null };
  }

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (segment === undefined || !WORKTREES_SEGMENT_NAMES.has(segment)) continue;

    let slugIndex = i + 1;
    while (slugIndex < segments.length && skip.has(segments[slugIndex] ?? "")) {
      slugIndex += 1;
    }
    const slug = segments[slugIndex];
    if (slug && slug.length > 0) {
      const previous = i > 0 ? segments[i - 1] : undefined;
      const namespace = previous ? stripLeadingDot(previous) : "";
      return {
        primary: slug,
        namespace: namespace.length > 0 ? namespace : null,
      };
    }
  }

  let basenameIndex = segments.length - 1;
  while (basenameIndex >= 0 && skip.has(segments[basenameIndex] ?? "")) {
    basenameIndex -= 1;
  }
  const fallback = basenameIndex >= 0 ? segments[basenameIndex] : segments[segments.length - 1];
  return { primary: fallback ?? "(unknown)", namespace: null };
}

/**
 * Group-aware version: computes worktree labels for every member of a
 * logical project group at once. Identifies segments that appear after
 * the `worktrees` marker in MULTIPLE members and treats them as shared
 * structural noise to skip — so e.g. when three checkouts of cmmd-forge
 * all contain a `cmmd-forge` directory layer at slightly different
 * positions, we land on the truly distinguishing slug for each
 * (`3dzk`, `0e93`, `trusting-hawking`).
 *
 * Output is parallel to `members` (same length, same order).
 */
export interface WorktreeLabelGroupMember {
  readonly cwd: string;
  readonly repositorySubpath?: string | null;
}

export function deriveWorktreeLabelsForGroup(
  members: readonly WorktreeLabelGroupMember[],
  /** Additional repo-name hints (e.g. `repositoryIdentity.name`). */
  extraSkipSegments: ReadonlySet<string> | null | undefined = null,
): readonly WorktreeLabel[] {
  if (members.length === 0) return [];

  // Tally segments that appear immediately after a `worktrees` marker
  // across members. Anything appearing in 2+ members is treated as a
  // structural layer that hides the real slug.
  const postMarkerCounts = new Map<string, number>();
  for (const member of members) {
    const root = trimRepoSubpath(member.cwd, member.repositorySubpath ?? null);
    const segments = splitPosixSegments(root);
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      if (segment === undefined || !WORKTREES_SEGMENT_NAMES.has(segment)) continue;
      const seen = new Set<string>();
      // Tally every segment after the marker (not just the first one)
      // because layouts vary in where the structural layer sits.
      for (let j = i + 1; j < segments.length; j++) {
        const candidate = segments[j];
        if (candidate === undefined) continue;
        if (seen.has(candidate)) continue;
        seen.add(candidate);
        postMarkerCounts.set(candidate, (postMarkerCounts.get(candidate) ?? 0) + 1);
      }
      break;
    }
  }

  const sharedSegments = new Set<string>();
  for (const [segment, count] of postMarkerCounts) {
    if (count >= 2) sharedSegments.add(segment);
  }
  if (extraSkipSegments) {
    for (const segment of extraSkipSegments) sharedSegments.add(segment);
  }

  return members.map((member) =>
    deriveWorktreeLabel(member.cwd, member.repositorySubpath ?? null, sharedSegments),
  );
}

/** Render a label as a single string (for tooltips / select rows). */
export function formatWorktreeLabel(label: WorktreeLabel): string {
  return label.namespace ? `${label.namespace} / ${label.primary}` : label.primary;
}
